import ApiClient from 'api-client/ApiClient'
import type { ResourceClasses } from 'api-client/Placement'
import { isAdmin } from 'app/plugins/infrastructure/components/common/helpers'
import store from 'app/store'
import ActionsSet from 'core/actions/ActionsSet'
import CreateAction from 'core/actions/CreateAction'
import CustomAction from 'core/actions/CustomAction'
import DeleteAction from 'core/actions/DeleteAction'
import ListAction from 'core/actions/ListAction'
import UpdateAction from 'core/actions/UpdateAction'
import { cacheActions } from 'core/caching/cacheReducers'
import { sessionStoreKey } from 'core/session/sessionReducers'
import { isValidIpv4Address } from 'core/utils/validators'
import DataKeys, { entityNamesByKey } from 'k8s/DataKeys'
import { isAdvancedConfigurationEnabled } from './hosts/advanced-overrides/advancedConfigurationFlag'
import type {
  AdvancedOverridesPatch,
  DryRunResult,
  OverridesState,
} from './hosts/advanced-overrides/models'
import type { AllocationCandidateItem } from './models'

const { dispatch } = store
const { haMgr, resMgr, nova, placement, sidekick, cinder } = ApiClient.getInstance()

/** Compute schedulable capacity from raw Placement inventory: (total - reserved) × ratio */
const inventoryToCandidate = (
  uuid: string,
  hostname: string,
  inventories: Record<string, { total?: number; reserved?: number; allocation_ratio?: number }>,
): AllocationCandidateItem | null => {
  const vcpu = inventories.VCPU
  if (!vcpu) return null // not a compute host
  const mem = inventories.MEMORY_MB ?? {}
  return {
    uuid,
    hostname,
    vcpuCapacity: Math.floor(
      ((vcpu.total ?? 0) - (vcpu.reserved ?? 0)) * (vcpu.allocation_ratio ?? 1),
    ),
    memoryCapacity: Math.floor(
      ((mem.total ?? 0) - (mem.reserved ?? 0)) * (mem.allocation_ratio ?? 1),
    ),
    diskCapacity: 0,
    diskUsed: 0,
  }
}

export const getSingleResmgrHost = (id) => {
  return resMgr.getResmgrHostV2(id)
}

export const resmgrActions = ActionsSet.make<DataKeys.ResmgrHosts>({
  uniqueIdentifier: 'id',
  entityName: entityNamesByKey.ResmgrHosts,
  cacheKey: DataKeys.ResmgrHosts,
})

export const allocationCandidateActions = ActionsSet.make<DataKeys.AllocationCandidates>({
  uniqueIdentifier: 'uuid',
  entityName: 'Allocation Candidates',
  cacheKey: DataKeys.AllocationCandidates,
})

/**
 * Fetches ratio-adjusted vCPU/memory/disk capacity for all hypervisors via
 * the Placement API.
 *
 * Primary source: GET /allocation_candidates?resources=VCPU:1,MEMORY_MB:1
 * — returns provider_summaries with capacity values that already account for
 *   allocation ratios.
 *
 * Supplementary: Fully-allocated hosts (0 free VCPU or MEMORY_MB) are absent
 * from allocation_candidates. For those, we fall back to per-provider
 * GET /resource_providers/{uuid}/inventories and compute schedulable capacity
 * as (total - reserved) × allocation_ratio. Usage data comes from Nova
 * hypervisor values rather than a separate Placement /usages call.
 *
 * Concurrency note: For large fully-allocated fleets the sequential
 * per-provider inventory fetches can race with in-flight scheduling
 * decisions, so returned capacities are best-effort snapshots.
 */
export const listAllocationCandidates = allocationCandidateActions.add(
  new ListAction<DataKeys.AllocationCandidates>(async () => {
    try {
      const { providerSummaries, uuidToHostname } = await placement.getAllocationCandidates()

      // Items from allocation_candidates (hosts with free capacity)
      const candidateItems = Object.entries(providerSummaries)
        .filter(([, summary]) => summary.parent_provider_uuid === null)
        .map(
          ([uuid, summary]): AllocationCandidateItem => ({
            uuid,
            hostname: uuidToHostname[uuid] ?? uuid,
            vcpuCapacity: summary.resources?.['VCPU']?.capacity ?? 0,
            vcpuUsed: summary.resources?.['VCPU']?.used ?? 0,
            memoryCapacity: summary.resources?.['MEMORY_MB']?.capacity ?? 0,
            memoryUsed: summary.resources?.['MEMORY_MB']?.used ?? 0,
            diskCapacity: summary.resources?.['DISK_GB']?.capacity ?? 0,
            diskUsed: summary.resources?.['DISK_GB']?.used ?? 0,
          }),
        )

      // Identify providers missing from allocation_candidates (fully-allocated).
      // Use ALL providerSummaries keys (including PCI children) as the covered set
      // so child devices are never mistaken for missing hosts.
      const coveredUuids = new Set(Object.keys(providerSummaries))
      const missingUuids = Object.keys(uuidToHostname).filter((uuid) => !coveredUuids.has(uuid))

      // Fetch inventories for missing providers in batches (capacity only).
      // Usage omitted — selector falls back to Nova hypervisor values.
      const BATCH_SIZE = 10
      const missingItems: (AllocationCandidateItem | null)[] = []
      for (let i = 0; i < missingUuids.length; i += BATCH_SIZE) {
        const batch = missingUuids.slice(i, i + BATCH_SIZE)
        const batchResults = await Promise.all(
          batch.map(async (uuid): Promise<AllocationCandidateItem | null> => {
            try {
              const resp = await placement.getResourceInventories({ id: uuid })
              return inventoryToCandidate(
                uuid,
                uuidToHostname[uuid] ?? uuid,
                resp?.inventories ?? {},
              )
            } catch {
              return null
            }
          }),
        )
        missingItems.push(...batchResults)
      }

      return [...candidateItems, ...missingItems.filter(Boolean)] as AllocationCandidateItem[]
    } catch (e) {
      console.error('[listAllocationCandidates] Failed to fetch allocation candidates:', e)
      return []
    }
  }),
)

export const listResmgrHosts = resmgrActions.add(
  new ListAction<DataKeys.ResmgrHosts>(async () => {
    const v1Hosts = await resMgr.getResmgrHosts()
    // ACL-FR-002: only ask for the Beta overrides when the feature is on
    const v2Hosts = await resMgr.getHostsv2(
      isAdvancedConfigurationEnabled(store.getState()[sessionStoreKey]?.features),
    )
    const hosts = v2Hosts.map((host) => {
      const v1Host = v1Hosts.find((h) => h.id === host.id)
      return {
        ...host,
        v1Roles: v1Host?.roles,
        v1RoleDetails: v1Host?.roles_status_details,
        filesystems: v1Host?.extensions?.resource_usage?.data?.filesystems,
        temp_storage: v1Host?.extensions?.resource_usage?.data?.temp_storage,
      }
    })
    return hosts
  })
    .addDependency(DataKeys.OpenstackVirtualMachinesLite)
    .addDependency(DataKeys.ClusterHostGroupsAllTenants)
    .addDependency(DataKeys.HostAggregates)
    .addDependency(DataKeys.Hypervisors)
    .addDependency(DataKeys.AllocationCandidates)
    .addDependency(DataKeys.StorageServiceDetails),
)

export const updateResmgrHost = resmgrActions.add(
  new UpdateAction<DataKeys.ResmgrHosts, { id: string }>(async ({ id }) => {
    const v1Host = await resMgr.getResmgrHostV1(id)
    const v2Host = await resMgr.getResmgrHostV2(id) //This is V2 Host calls
    return {
      ...v2Host,
      v1Roles: v1Host?.roles,
      v1RoleDetails: v1Host?.roles_status_details,
      filesystems: v1Host?.extensions?.resource_usage?.data?.filesystems,
      temp_storage: v1Host?.extensions?.resource_usage?.data?.temp_storage,
    }
  }),
)

export const deauthResmgrHost = resmgrActions.add(
  new CustomAction<DataKeys.ResmgrHosts, { id: string }>('deauthorizeHost', async ({ id }) => {
    return resMgr.unauthorizeHost(id)
  }),
)

const getIpForInterface = (host, interfaceName, settings) => {
  // try bridge name too, in case ip already moved to bridge
  const activePhysicalNetworksInterfaces =
    settings.blueprint?.config?.physicalNetworksInterfaceMap?.filter((network) => network.isActive)
  const bridgeMap = activePhysicalNetworksInterfaces?.reduce((accum, phyNetInterface) => {
    return {
      ...accum,
      [phyNetInterface.interface]: phyNetInterface.bridgeName,
    }
  }, {})
  const ifaces = host.interfaces
  return ifaces.find((iface) => {
    const ifaceMatch = iface.name === interfaceName && isValidIpv4Address(iface.ip)
    const bridgeMatch = iface.name === bridgeMap[interfaceName] && isValidIpv4Address(iface.ip)
    return ifaceMatch || bridgeMatch
  })?.ip
}

const keyForInterface = {
  vmNetwork: 'vmnet_interface',
  managementNetwork: 'mgmt_interface',
}

export const assignHypervisorRole = async ({ host, hostcluster }) => {
  return resMgr.assignHypervisorRole({ id: host.id, hostcluster })
}

export const assignImageLibraryRole = async ({ host }) => {
  return resMgr.assignImageLibraryRole({ id: host.id })
}

export const assignPersistentStorageRole = async ({ host, body }) => {
  return resMgr.assignPersistentStorageRole({ id: host.id, body })
}

export const deauthorizeHypervisorRole = async ({ host }) => {
  return resMgr.deauthorizeHypervisorRole({ id: host.id })
}

export const deauthorizeImageLibraryRole = async ({ host }) => {
  return resMgr.deauthorizeImageLibraryRole({ id: host.id })
}

export const deauthorizePersistentStorageRole = async ({ host }) => {
  return resMgr.deauthorizePersistentStorageRole({ id: host.id })
}

export const assignHostConfig = async ({ hostId, configId }) => {
  return resMgr.assignHostConfig({ hostId, configId })
}

export const unassignHostConfig = async ({ hostId, configId }) => {
  return resMgr.unassignHostConfig({ hostId, configId })
}

export const updateHypervisorRole = async ({ host, settings, storageType = null }) => {
  const hostLivenessIp = getIpForInterface(
    host,
    settings?.blueprint?.config?.[
      keyForInterface[settings.blueprint.config.host_liveness_interface]
    ],
    settings,
  )
  const hostIp = getIpForInterface(
    host,
    settings?.blueprint?.config?.[keyForInterface[settings.blueprint.config.console_interface]],
    settings,
  )
  const ostackhostBody =
    storageType === 'ceph'
      ? {
          cluster_ip: hostLivenessIp,
          consul_ip: '',
          instances_path: settings?.blueprint?.config?.vm_storage_path,
          novncproxy_base_url: `http://${hostIp}:6080/vnc_auto.html`,
          live_migration_inbound_addr: hostLivenessIp,
          // additional ceph properties
          rbd_secret_uuid: settings?.blueprint?.config?.cephBackendConf?.rbd_secret_uuid,
          ceph_client_cinder_secret:
            settings?.blueprint?.config?.cephBackendConf?.ceph_client_cinder_secret,
        }
      : {
          cluster_ip: hostLivenessIp,
          // consul_ip: hostIp,
          // cluster_ip: '',
          consul_ip: '',
          instances_path: settings?.blueprint?.config?.vm_storage_path,
          novncproxy_base_url: `http://${hostIp}:6080/vnc_auto.html`,
          // live_migration_inbound_addr: liveMigrationIp,
          live_migration_inbound_addr: hostLivenessIp,
        }
  return updateHostRole({ hostId: host.id, role: 'pf9-ostackhost-neutron', body: ostackhostBody })
}

export const updateNeutronRoles = async ({ host, settings }) => {
  // const hostIp = getIpForInterface(host, settings?.blueprint?.config?.tunneling_interface)
  const networkingBackend = settings?.blueprint?.config?.networkingType || 'ovn'
  const hasVlanUnderlay = settings?.neutronServer?.network_vlan_ranges?.includes(':')
  const underlayLabel = hasVlanUnderlay
    ? settings?.neutronServer?.network_vlan_ranges
        ?.split(',')
        ?.find((range) => range.includes(':'))
        .split(':')?.[0]
    : null
  const hostIp =
    getIpForInterface(host, settings?.blueprint?.config?.tunneling_interface, settings) ||
    getIpForInterface(host, settings?.blueprint?.config?.vmnet_interface, settings) ||
    getIpForInterface(host, settings?.blueprint?.config?.mgmt_interface, settings)

  // Active PhysicalInterfaces mean they are being used by VM Network or underlay Networks
  const physicalNetworksInterfaceMap =
    settings?.blueprint?.config?.physicalNetworksInterfaceMap || []
  const activePhysicalNetworksInterfaces = physicalNetworksInterfaceMap?.filter(
    (network) => network.isActive,
  )
  const bridgeMappings = activePhysicalNetworksInterfaces.map(
    (network) => `${network.label}:${network.bridgeName}`,
  )
  const interfaceMappings = activePhysicalNetworksInterfaces?.map(
    (network) => `${network.bridgeName}:${network.interface}`,
  )
  const ovnControllerBody = {
    // bridge_mappings: 'vm-net:b-vm-net',
    bridge_mappings: bridgeMappings.join(',') || 'unset',
    ovn_encap_ip: hostIp || 'unset',
    ovn_encap_type: settings?.blueprint?.config?.underlayType === 'vxlan' ? 'vxlan' : 'geneve',
    // interface_mappings: `b-vm-net:${settings?.blueprint?.config?.vmnet_interface}`,
    interface_mappings: interfaceMappings.join(',') || 'unset',
  }
  const ovsAgentBody = {
    bridge_mappings: bridgeMappings.join(','),
    // If underlay type is vlan and there is no vlan underlay, false, otherwise it will be true
    enable_tunneling:
      settings?.blueprint?.config?.underlayType === 'vlan' && !hasVlanUnderlay ? 'False' : 'True',
    tunnel_types: settings?.blueprint?.config?.underlayType,
    local_ip: hostIp,
    net_type: 'vlan,vxlan',
    enable_distributed_routing: settings?.neutronServer?.router_distributed,
    interface_mappings: interfaceMappings.join(','),
  }
  const l3AgentBody = {
    agent_mode: 'dvr_snat',
  }
  const dhcpAgentBody = {
    dnsmasq_dns_servers: '8.8.8.8',
    dns_domain: settings?.neutronServer?.dns_domain,
  }

  // if ovs then use ovs-agent & l3-agent & dhcp-agent & pf9-neutron-metadata-agent
  return networkingBackend === 'ovs'
    ? Promise.allSettled([
        updateHostRole({
          hostId: host.id,
          role: 'pf9-neutron-ovs-agent',
          body: ovsAgentBody,
        }),
        updateHostRole({
          hostId: host.id,
          role: 'pf9-neutron-l3-agent',
          body: l3AgentBody,
        }),
        updateHostRole({
          hostId: host.id,
          role: 'pf9-neutron-dhcp-agent',
          body: dhcpAgentBody,
        }),
        updateHostRole({
          hostId: host.id,
          role: 'pf9-neutron-metadata-agent',
          body: {},
        }),
      ])
    : Promise.allSettled([
        updateHostRole({
          hostId: host.id,
          role: 'pf9-neutron-ovn-controller',
          body: ovnControllerBody,
        }),
        updateHostRole({ hostId: host.id, role: 'pf9-neutron-ovn-metadata-agent', body: {} }),
      ])
}

export const updateImageLibraryRole = async ({ host, settings }) => {
  const body = {
    // if hypervisor role is present, use the bridge name
    // for the associated interface instead of interface
    // from interface_mappings property from ovs-agent or ovn-controller roles
    endpoint_address: getIpForInterface(
      host,
      settings?.blueprint?.config?.[keyForInterface[settings.blueprint.config.imagelib_interface]],
      settings,
    ),
    filesystem_store_datadir: settings?.blueprint?.config?.imagelib_path,
    update_public_glance_endpoint: 'true',
    // create_glance_cluster_endpoint: 'false',
  }
  return updateHostRole({ hostId: host.id, role: 'pf9-glance-role', body })
}

export const updateCinderRoleLvm = async ({ host, settings }) => {
  await updateHostRole({ hostId: host.id, role: 'pf9-cindervolume-base', body: {} })
  const body = {
    // target_ip_address: getIpForInterface(host, settings?.blueprint?.config?.iscsi_interface),
    target_ip_address: getIpForInterface(
      host,
      settings?.blueprint?.config?.iscsi_interface,
      settings,
    ),
    volume_driver: 'cinder.volume.drivers.lvm.LVMVolumeDriver',
    volume_backend_name: 'lvm-backend',
  }
  return updateHostRole({ hostId: host.id, role: 'pf9-cindervolume-lvm', body })
}

export const updateCinderRoleNfs = async ({ host, settings }) => {
  await updateHostRole({ hostId: host.id, role: 'pf9-cindervolume-base', body: {} })
  const body = {
    nfs_mount_points: settings?.blueprint?.config?.nfsBackendConf?.nfs_mount_points,
    volume_backend_name: 'nfs-backend',
    nfs_shares_config: '/opt/pf9/etc/pf9-cindervolume-base/conf.d/nfs_shares',
  }
  return updateHostRole({ hostId: host.id, role: 'pf9-cindervolume-nfs', body })
}

export const updateCinderRoleCeph = async ({ host, settings }) => {
  await updateHostRole({ hostId: host.id, role: 'pf9-cindervolume-base', body: {} })
  const body = {
    volume_backend_name: 'ceph-backend',
    rbd_pool: 'volumes',
    rbd_user: 'cinder',
    rbd_secret_uuid: settings?.blueprint?.config?.cephBackendConf?.rbd_secret_uuid,
  }
  return updateHostRole({ hostId: host.id, role: 'pf9-cindervolume-ceph', body })
}

export const updateCinderRoleOther = async ({ host, settings }) => {
  await updateHostRole({ hostId: host.id, role: 'pf9-cindervolume-base', body: {} })
  const body = {
    volume_backend_name: 'custom',
    volume_driver: settings?.blueprint?.config?.customBackendConf?.volume_driver,
  }
  return updateHostRole({ hostId: host.id, role: 'pf9-cindervolume-other', body })
}

export const updateCinderRoleNetApp = async ({ host, settings }) => {
  await updateHostRole({ hostId: host.id, role: 'pf9-cindervolume-base', body: {} })
  const body = {
    volume_backend_name: 'netapp_backend',
    netapp_server_hostname: settings?.blueprint?.config?.netAppBackendConf?.netapp_server_hostname,
    netapp_login: settings?.blueprint?.config?.netAppBackendConf?.netapp_login,
    netapp_password: settings?.blueprint?.config?.netAppBackendConf?.netapp_password,
    netapp_vserver: settings?.blueprint?.config?.netAppBackendConf?.netapp_vserver,
    netapp_server_port: settings?.blueprint?.config?.netAppBackendConf?.netapp_server_port,
    netapp_storage_protocol:
      settings?.blueprint?.config?.netAppBackendConf?.netapp_storage_protocol,
    ...(settings?.blueprint?.config?.netAppBackendConf?.netapp_lun_space_reservation && {
      netapp_lun_space_reservation: 'enabled',
    }),
    ...(settings?.blueprint?.config?.netAppBackendConf?.netapp_pool_name_search_pattern && {
      netapp_pool_name_search_pattern:
        settings.blueprint.config.netAppBackendConf.netapp_pool_name_search_pattern,
    }),
    ...(settings?.blueprint?.config?.netAppBackendConf?.reserved_percentage && {
      reserved_percentage: settings.blueprint.config.netAppBackendConf.reserved_percentage,
    }),
    ...(settings?.blueprint?.config?.netAppBackendConf?.max_over_subscription_ratio && {
      max_over_subscription_ratio:
        settings.blueprint.config.netAppBackendConf.max_over_subscription_ratio,
    }),
    ...(settings?.blueprint?.config?.netAppBackendConf?.use_multipath_for_image_xfer && {
      use_multipath_for_image_xfer: 'True',
    }),
    ...(settings?.blueprint?.config?.netAppBackendConf?.enforce_multipath_for_image_xfer && {
      enforce_multipath_for_image_xfer: 'True',
    }),
  }
  return updateHostRole({ hostId: host.id, role: 'pf9-cindervolume-netapp', body })
}

export const updateNeutronBaseRole = async ({ host }) => {
  try {
    return updateHostRole({ hostId: host.id, role: 'pf9-neutron-base', body: {} })
  } catch (err) {
    throw new Error(err)
  }
}

export const updateRemoteSupportRole = async ({ host }) => {
  return updateHostRole({ hostId: host.id, role: 'pf9-support', body: {} })
}

export const updateCeilometerRole = async ({ host, settings }) => {
  return updateHostRole({
    hostId: host.id,
    role: 'pf9-ceilometer',
    body: {},
  })
}

export const updateVmwHypervisorRole = async ({ host, body }) => {
  return updateHostRole({ hostId: host.id, role: 'pf9-ostackhost-neutron-vmw', body })
}

export const removeVmwHypervisorRole = async ({ host }) => {
  return removeHostRole({ hostId: host.id, role: 'pf9-ostackhost-neutron-vmw' })
}

export const updateVmwImageLibraryRole = async ({ host, body }) => {
  return updateHostRole({ hostId: host.id, role: 'pf9-glance-role-vmw', body })
}

export const removeVmwImageLibraryRole = async ({ host }) => {
  return removeHostRole({ hostId: host.id, role: 'pf9-glance-role-vmw' })
}

export const updateDiscoveryRole = async ({ host, body }) => {
  return updateHostRole({ hostId: host.id, role: 'pf9-discovery-role', body })
}

export const removeDiscoveryRole = async ({ host }) => {
  return removeHostRole({ hostId: host.id, role: 'pf9-discovery-role' })
}

export const removeHypervisorRole = async ({ hostId, settings }) => {
  const networkingBackend = settings?.blueprint?.config?.networkingType || 'ovn'

  // Must remove in order
  if (networkingBackend === 'ovs') {
    await Promise.allSettled([
      removeHostRole({ hostId, role: 'pf9-ceilometer' }),
      removeHostRole({ hostId, role: 'pf9-neutron-ovs-agent' }),
      removeHostRole({ hostId, role: 'pf9-neutron-l3-agent' }),
      removeHostRole({ hostId, role: 'pf9-neutron-dhcp-agent' }),
      removeHostRole({ hostId, role: 'pf9-neutron-metadata-agent' }),
    ])
  } else {
    await Promise.allSettled([
      removeHostRole({ hostId, role: 'pf9-ceilometer' }),
      removeHostRole({ hostId, role: 'pf9-neutron-ovn-controller' }),
      removeHostRole({ hostId, role: 'pf9-neutron-ovn-metadata-agent' }),
    ])
  }
  await removeHostRole({ hostId, role: 'pf9-ostackhost-neutron' })
  return removeHostRole({ hostId, role: 'pf9-neutron-base' })
}

export const removeImageLibraryRole = async ({ hostId }) => {
  return removeHostRole({ hostId, role: 'pf9-glance-role' })
}

export const removeCinderRoleLvm = async ({ hostId }) => {
  await removeHostRole({ hostId, role: 'pf9-cindervolume-lvm' })
  return removeHostRole({ hostId, role: 'pf9-cindervolume-base' })
}

export const removeCinderRoleNfs = async ({ hostId }) => {
  await removeHostRole({ hostId, role: 'pf9-cindervolume-nfs' })
  return removeHostRole({ hostId, role: 'pf9-cindervolume-base' })
}

export const removeCinderRoleCeph = async ({ hostId }) => {
  await removeHostRole({ hostId, role: 'pf9-cindervolume-ceph' })
  return removeHostRole({ hostId, role: 'pf9-cindervolume-base' })
}

export const removeCinderRoleOther = async ({ hostId }) => {
  await removeHostRole({ hostId, role: 'pf9-cindervolume-other' })
  return removeHostRole({ hostId, role: 'pf9-cindervolume-base' })
}

export const removeCinderRoleNetApp = async ({ hostId }) => {
  await removeHostRole({ hostId, role: 'pf9-cindervolume-netapp' })
  return removeHostRole({ hostId, role: 'pf9-cindervolume-base' })
}

export const removeRemoteSupportRole = async ({ hostId }) => {
  return removeHostRole({ hostId, role: 'pf9-support' })
}

export const getResmgrRoleConfig = async ({ role, host }) => {
  return resMgr.getRole(host.id, role)
}

export const updateHostRole = async ({ hostId, role, body }) => {
  return resMgr.addRole(hostId, role, body)
}

export const assignDns = async ({ host }) => {
  return resMgr.assignDns({ hostId: host.id })
}

export const removeDns = async ({ hostId }) => {
  return resMgr.unassignDns(hostId)
}

export const removeHostRole = async ({ hostId, role }) => {
  return resMgr.removeRole(hostId, role)
}

export const getBlueprints = async () => {
  return resMgr.getBlueprints()
}

// Cached blueprint list. The blueprint holds `imageLibraryStores` — the authoritative set of
// configured image stores — which the Images page reads to decide "copy to all stores". Stores
// rarely change, so caching it avoids refetching on every visit; call invalidateBlueprintsCache()
// after saving a blueprint so a store change is picked up.
export const blueprintActions = ActionsSet.make<DataKeys.Blueprints>({
  uniqueIdentifier: 'name',
  entityName: entityNamesByKey.Blueprints,
  cacheKey: DataKeys.Blueprints,
})

export const listBlueprints = blueprintActions.add(
  new ListAction<DataKeys.Blueprints>(async () => {
    // Return [] on error so a site with no blueprint yet (e.g. a brand-new user who opens Images
    // before configuring anything) doesn't break — readers just see "no configured stores".
    try {
      return (await resMgr.getBlueprints()) as any[]
    } catch (err) {
      return []
    }
  }),
)

// Drop the cached blueprint(s) so the next read refetches — call this after a blueprint save so a
// change to the configured stores is reflected everywhere that reads the cache.
export const invalidateBlueprintsCache = () => {
  dispatch(cacheActions.clearCache({ cacheKey: DataKeys.Blueprints }))
}

export const getBlueprint = async ({ name }) => {
  return resMgr.getBlueprint({ name })
}

export const createBlueprint = async ({ name, body }) => {
  return resMgr.createBlueprint({ body })
}

export const updateBlueprint = async ({ name, body }) => {
  return resMgr.updateBlueprint({ name, body })
}

// Todo: Convert this into a action set
export const getHostNetworkConfigs = async () => {
  return resMgr.getHostNetworkConfigs()
}

export const createHostNetworkConfigs = async (body) => {
  return resMgr.createHostNetworkConfigs(body)
}

export const updateHostNetworkConfig = async ({ id, body }) => {
  return resMgr.updateHostNetworkConfig({ id, body })
}

export const deleteHostNetworkConfig = async ({ id }) => {
  return resMgr.deleteHostNetworkConfig(id)
}

export const getResmgrServiceConfig: any = async (service) => {
  return resMgr.getService(service)
}

export const updateResmgrServiceConfig = async (service, body) => {
  try {
    const response = await resMgr.updateService(service, body)
    return response
  } catch (e) {
    return new Error(e?.err?.message)
  }
}

export const getHostAggregateConfig: any = async (aggregateId) => {
  return haMgr.getHaConfig(aggregateId)
}

export const enableHighAvailability: any = async (aggregateId) => {
  return haMgr.enableHa(aggregateId)
}

export const disableHighAvailability: any = async (aggregateId) => {
  return haMgr.disableHa(aggregateId)
}

export const hypervisorActions = ActionsSet.make<DataKeys.Hypervisors>({
  uniqueIdentifier: 'id',
  entityName: entityNamesByKey.Hypervisors,
  cacheKey: DataKeys.Hypervisors,
})

export const listHypervisors = hypervisorActions.add(
  new ListAction<DataKeys.Hypervisors>(async () => {
    // Admin only API
    if (!isAdmin()) {
      return []
    }
    return nova.getHypervisors()
  }),
)

export const enableHypervisorScheduling = hypervisorActions.add(
  new CustomAction<DataKeys.Hypervisors, { id: string; body?: any }>(
    'enableHypervisorScheduling',
    async ({ id, body }) => {
      await nova.enableHypervisorScheduling(body)
      const hypervisor = await nova.getHypervisor(id)
      return hypervisor
    },
    (result, { id }) => {
      // Update the cluster in the cache
      dispatch(
        cacheActions.updateItem({
          uniqueIdentifier: 'id',
          cacheKey: DataKeys.Hypervisors,
          params: { id }, // TODO: Double check this works
          item: result,
        }),
      )
    },
  ),
)

export const disableHypervisorScheduling = hypervisorActions.add(
  new CustomAction<DataKeys.Hypervisors, { id: string; body?: any }>(
    'disableHypervisorScheduling',
    async ({ id, body }) => {
      await nova.disableHypervisorScheduling(body)
      const hypervisor = await nova.getHypervisor(id)
      return hypervisor
    },
    (result, { id }) => {
      // Update the cluster in the cache
      dispatch(
        cacheActions.updateItem({
          uniqueIdentifier: 'id',
          cacheKey: DataKeys.Hypervisors,
          params: { id },
          item: result,
        }),
      )
    },
  ),
)

export const enableMaintenanceMode = hypervisorActions.add(
  new CustomAction<DataKeys.Hypervisors, { id: string; body?: any }>(
    'enableMaintenanceMode',
    async ({ id, body }) => {
      await nova.enableMaintenanceMode(body)
      const hypervisor = await nova.getHypervisor(id)
      return hypervisor
    },
    (result, { id }) => {
      // Update the cluster in the cache
      dispatch(
        cacheActions.updateItem({
          uniqueIdentifier: 'id',
          cacheKey: DataKeys.Hypervisors,
          params: { id },
          item: result,
        }),
      )
    },
  ),
)

export const initiateVolumeHostTransfer = async ({ sourceHostId, targetHostId, dryRun }) => {
  return await cinder.initiateVolumeHostTransfer({ sourceHostId, targetHostId, dryRun })
}

export const hostAggregateActions = ActionsSet.make<DataKeys.HostAggregates>({
  uniqueIdentifier: 'id',
  entityName: entityNamesByKey.HostAggregates,
  cacheKey: DataKeys.HostAggregates,
})

export const listHostAggregates = hostAggregateActions.add(
  new ListAction<DataKeys.HostAggregates>(async () => {
    return nova.getHostAggregates()
  })
    .addDependency(DataKeys.ResmgrHosts)
    .addDependency(DataKeys.Hypervisors)
    .addDependency(DataKeys.HostNetworkConfigs),
)

export const createHostAggregate = hostAggregateActions.add(
  new CreateAction<DataKeys.HostAggregates, { body }>(async ({ body }) => {
    const created = await nova.createHostAggregate(body)
    const aggregateId = created.id
    const newAggregate = await nova.getHostAggregate(aggregateId)
    return newAggregate
  }),
)

export const updateHostAggregate = hostAggregateActions.add(
  new UpdateAction<
    DataKeys.HostAggregates,
    {
      id: string
      body: unknown
    }
  >(async ({ id, body }) => {
    const updatedHostAggregate = await nova.updateHostAggregate({ id, body })
    return updatedHostAggregate
  }),
)

export const addHostToAggregate = hostAggregateActions.add(
  new CustomAction<DataKeys.HostAggregates, { id: string; body?: any }>(
    'addHostToAggregate',
    async ({ id, body }) => {
      await nova.hostAggregateAction({ id, body })
      const aggregate = await nova.getHostAggregate(id)
      return aggregate
    },
    (result, { id }) => {
      // Update the cluster in the cache
      dispatch(
        cacheActions.updateItem({
          uniqueIdentifier: 'id',
          cacheKey: DataKeys.HostAggregates,
          params: { id }, // TODO: Double check this works
          item: result,
        }),
      )
    },
  ),
)

export const removeHostFromAggregate = hostAggregateActions.add(
  new CustomAction<DataKeys.HostAggregates, { id: string; body?: any }>(
    'removeHostFromAggregate',
    async ({ id, body }) => {
      await nova.hostAggregateAction({ id, body })
      const aggregate = await nova.getHostAggregate(id)
      return aggregate
    },
    (result, { id }) => {
      // Update the cluster in the cache
      dispatch(
        cacheActions.updateItem({
          uniqueIdentifier: 'id',
          cacheKey: DataKeys.HostAggregates,
          params: { id }, // TODO: Double check this works
          item: result,
        }),
      )
    },
  ),
)

export const updateHostAggregateMetadata = hostAggregateActions.add(
  new CustomAction<DataKeys.HostAggregates, { id: string; body?: any }>(
    'updateHostAggregateMetadata',
    async ({ id, body }) => {
      await nova.hostAggregateAction({ id, body })
      const aggregate = await nova.getHostAggregate(id)
      return aggregate
    },
    (result, { id }) => {
      // Update the cluster in the cache
      dispatch(
        cacheActions.updateItem({
          uniqueIdentifier: 'id',
          cacheKey: DataKeys.HostAggregates,
          params: { id }, // TODO: Double check this works
          item: result,
        }),
      )
    },
  ),
)

export const deleteHostAggregate = hostAggregateActions.add(
  new DeleteAction<DataKeys.HostAggregates, { id: string }>(async ({ id }) => {
    await nova.deleteHostAggregate(id)
  }),
)

export const checkVMHAStatusForCluster = (clusterId) => {
  return haMgr.getVMHAStatusForCluster(clusterId)
}

export const getVmCountForHost = (hostId) => {
  return nova.getVirtualMachinesCount(`all_tenants=true&deleted=False&host=${hostId}`)
}

export const getVmsForHost = (hostId) => {
  return nova.getInstancesForHost(hostId)
}

export const getVmsWithTag = (tag) => {
  return nova.getInstancesWithTag(tag)
}

export const hostNetworkConfigActions = ActionsSet.make<DataKeys.HostNetworkConfigs>({
  uniqueIdentifier: 'id',
  entityName: entityNamesByKey.HostNetworkConfigs,
  cacheKey: DataKeys.HostNetworkConfigs,
})

export const listHostNetworkConfigs = hostNetworkConfigActions.add(
  new ListAction<DataKeys.HostNetworkConfigs>(async () => {
    return resMgr.getHostNetworkConfigs()
  }),
)

export const getResources = async () => {
  return placement.getResources()
}

export const getResourceClasses = async (): Promise<ResourceClasses[]> => {
  return placement.getResourceClasses()
}

export const listResourceInventory = async ({ id }) => {
  return placement.getResourceInventories({ id })
}

export const updateResourceInventory = async ({ id, body }) => {
  const updateResourceInventory = await placement.updateResourceInventory({ id, body })
  return updateResourceInventory
}

export const getTraits = async () => {
  return placement.getTraits()
}

export const updateTrait = async ({ trait }) => {
  return placement.updateTrait(trait)
}

export const updateResourceTraits = async ({ trait, resourceId }) => {
  const response = await placement.getResourceTraits({ id: resourceId })
  const currentResourceTraits = response?.traits
  if (currentResourceTraits.includes(trait)) {
    return true
  }
  return placement.updateResourceTraits({
    id: resourceId,
    body: {
      resource_provider_generation: response?.resource_provider_generation,
      traits: [...currentResourceTraits, trait],
    },
  })
}

export const generateSupportBundle = async ({ body }: { body: { hostIds: string[] } }) => {
  return await sidekick.generateSupportBundle({ body })
}

export const getSupportBundleStatus = async ({ bundleId }: { bundleId: string }) => {
  return await sidekick.getSupportBundleStatus({ bundleId })
}

export const downloadSupportBundle = async ({ bundleId }: { bundleId: string }) => {
  return await sidekick.downloadSupportBundle({ bundleId })
}

export const cancelSupportBundle = async ({ bundleId }: { bundleId: string }) => {
  return await sidekick.cancelSupportBundle({ bundleId })
}

export const overrideCatalogActions = ActionsSet.make<DataKeys.OverrideCatalog>({
  uniqueIdentifier: 'role',
  entityName: entityNamesByKey.OverrideCatalog,
  cacheKey: DataKeys.OverrideCatalog,
})

// AOM-FR-001/AOM-FR-002: fetch every role's override catalog in one call, cached
// for the session (no params) so the stepper and entry-point gate share one fetch.
// AOM-FR-014: a failed fetch raises an error toast with this title
export const listOverrideCatalog = overrideCatalogActions.add(
  new ListAction<DataKeys.OverrideCatalog>(
    async () => {
      const catalogsByRole = await resMgr.getOverrideCatalog()
      return Object.values(catalogsByRole)
    },
    { errorMessage: 'Could not load the Advanced Configuration catalog' },
  ),
)

export const hostRoleOverridesActions = ActionsSet.make<DataKeys.HostRoleOverrides>({
  uniqueIdentifier: ['host_id', 'role'],
  entityName: entityNamesByKey.HostRoleOverrides,
  cacheKey: DataKeys.HostRoleOverrides,
  indexBy: ['hostId', 'role'],
})

// Every overrides write resolves with the role's new OverridesState: swap that
// host/role's cache entry for it.
const updateOverridesCache = (
  result: unknown,
  { hostId, role }: { hostId: string; role: string },
) => {
  dispatch(
    cacheActions.updateItem({
      uniqueIdentifier: ['host_id', 'role'],
      cacheKey: DataKeys.HostRoleOverrides,
      params: { host_id: hostId, role },
      item: result as Record<string, unknown>,
    }),
  )
}

// AOM-FR-003/AOM-FR-010: current overrides + per-file status for one host/role.
// Cached by (hostId, role) params — used for both pre-fill and status polling.
export const listHostRoleOverrides = hostRoleOverridesActions.add(
  new ListAction<DataKeys.HostRoleOverrides, { hostId: string; role: string }>(
    async ({ hostId, role }) => {
      const state = await resMgr.getHostRoleOverrides(hostId, role)
      return [state]
    },
    // AOM-FR-014: a failed read raises an error toast with this title
    { errorMessage: 'Could not load the Advanced Configuration overrides' },
  ),
)

interface ReplaceOverridesBody {
  advanced_overrides: AdvancedOverridesPatch
  expected_version?: number
}

// AOM-FR-006/AOM-FR-008: preview-only — never touches the cache, result (including
// the server-computed `diff`, used by the Review step) is read straight off the
// action hook (success/error/result) by the caller.
export const dryRunHostRoleOverrides = hostRoleOverridesActions.add(
  new CustomAction<
    DataKeys.HostRoleOverrides,
    { hostId: string; role: string; body: ReplaceOverridesBody },
    DryRunResult
  >(
    'dryRunHostRoleOverrides',
    async ({ hostId, role, body }) => {
      const result = await resMgr.replaceHostRoleOverrides(hostId, role, body, { dryRun: true })
      return result as DryRunResult
    },
    () => undefined,
  ),
)

// AOM-FR-009/AOM-FR-011: submit (and retry, which resends this with the same
// body) a host/role's overrides. Updates only that host/role's cache entry.
export const replaceHostRoleOverrides = hostRoleOverridesActions.add(
  new CustomAction<
    DataKeys.HostRoleOverrides,
    { hostId: string; role: string; body: ReplaceOverridesBody }
  >(
    'replaceHostRoleOverrides',
    async ({ hostId, role, body }) => {
      return resMgr.replaceHostRoleOverrides(hostId, role, body) as Promise<OverridesState>
    },
    updateOverridesCache,
  ),
)

// AOM-FR-011: swap current/previous config for one host/role (the Revert action).
export const revertHostRoleOverridesAction = hostRoleOverridesActions.add(
  new CustomAction<
    DataKeys.HostRoleOverrides,
    { hostId: string; role: string; expectedVersion?: number }
  >(
    'revertHostRoleOverrides',
    async ({ hostId, role, expectedVersion }) => {
      return resMgr.revertHostRoleOverrides(hostId, role, { expectedVersion })
    },
    updateOverridesCache,
  ),
)

// HAC-FR-005, HAC-FR-006: drop all overrides for one host/role (restore to defaults).
export const deleteHostRoleOverridesAction = hostRoleOverridesActions.add(
  new CustomAction<
    DataKeys.HostRoleOverrides,
    { hostId: string; role: string; expectedVersion?: number }
  >(
    'deleteHostRoleOverrides',
    async ({ hostId, role, expectedVersion }) => {
      return resMgr.deleteHostRoleOverrides(hostId, role, { expectedVersion })
    },
    updateOverridesCache,
  ),
)

// HAC-FR-004: drop overrides for a single file of one host/role.
export const deleteHostFileOverridesAction = hostRoleOverridesActions.add(
  new CustomAction<
    DataKeys.HostRoleOverrides,
    { hostId: string; role: string; fileId: string; expectedVersion?: number }
  >(
    'deleteHostFileOverrides',
    async ({ hostId, role, fileId, expectedVersion }) => {
      return resMgr.deleteHostFileOverrides(hostId, role, fileId, { expectedVersion })
    },
    updateOverridesCache,
  ),
)

// HAC-FR-007: re-push the current overrides for one host/role.
export const reapplyHostRoleOverridesAction = hostRoleOverridesActions.add(
  new CustomAction<DataKeys.HostRoleOverrides, { hostId: string; role: string }>(
    'reapplyHostRoleOverrides',
    async ({ hostId, role }) => {
      return resMgr.reapplyHostRoleOverrides(hostId, role)
    },
    updateOverridesCache,
  ),
)
